import { useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Trash2, UserPlus, Calendar, CheckSquare, Square, X, Search,
    Archive, ArchiveRestore, Trophy, Sparkles, User, Star, Lock, Unlock,
} from "lucide-react";
// iter87.199.6 — CrownPointsSandbox import removed with the Crown Race
// excise (component file deleted). The Title Race replacement will
// need its own sandbox later; nothing to import in the meantime.
import { SiteAdminSchedule } from "@/components/SiteAdminSchedule";
import { useBrutalConfirm } from "@/components/BrutalConfirm";
import SiteAdminCreateLeagueModal from "@/components/SiteAdminCreateLeagueModal";
import SiteAdminDraftSnapshots from "@/components/SiteAdminDraftSnapshots";
import SiteAdminRenamePreviewModal from "@/components/SiteAdminRenamePreviewModal";
import LeagueParticipantsPanel from "@/components/site-admin/leagues/LeagueParticipantsPanel";
import LeagueTournamentsPanel from "@/components/site-admin/leagues/LeagueTournamentsPanel";

// iter84.70 — Rivalries tab, extracted from SiteAdmin.jsx. Owns the league
// list, search/tier filters, bulk selection + delete/features, per-league
// expansions (participants / tournaments), and the leagues-scoped modals
// (create league, schedule console, snapshots, rename preview).
export default function LeaguesTab({ admin, leagues, loadAll }) {
    const { confirm, hostElement: confirmHost } = useBrutalConfirm();
    const [participantsByLeague, setParticipantsByLeague] = useState({});
    const [expandedLeagueId, setExpandedLeagueId] = useState(null);
    // iter45c — Site Admin multi-tournament expansion.
    const [expandedTournamentsLeagueId, setExpandedTournamentsLeagueId] = useState(null);
    const [tournamentsByLeague, setTournamentsByLeague] = useState({});
    // iter63z'' — Site Admin → Create League modal
    const [createLeagueOpen, setCreateLeagueOpen] = useState(false);
    // Bulk selection state
    const [selectedLeagueIds, setSelectedLeagueIds] = useState(() => new Set());
    // iter84.4 (Phase 5) — Bulk-features modal state
    const [bulkDeleting, setBulkDeleting] = useState(false);
    // iter43f — Leagues tab search + tier filter
    const [leagueSearch, setLeagueSearch] = useState("");
    const [leagueTierFilter, setLeagueTierFilter] = useState("all"); // all | free | paid | comp
    // iter86.40 — Live vs Archived view. iter87.407 — added Closed.
    const [leagueView, setLeagueView] = useState("live"); // live | closed | archived
    // Schedule console (per-tournament site-admin CRUD)
    const [scheduleConsole, setScheduleConsole] = useState(null); // { tid, tournament } | null
    const [snapshotsModal, setSnapshotsModal] = useState(null); // { tid, name } | null
    // iter63z''''' — WCKO auto-rename preview modal.
    const [renamePreview, setRenamePreview] = useState(null); // { tid, name } | null

    const expandLeague = async (lid) => {
        if (expandedLeagueId === lid) { setExpandedLeagueId(null); return; }
        setExpandedLeagueId(lid);
        if (!participantsByLeague[lid]) {
            try {
                const p = await api.siteListParticipants(lid);
                setParticipantsByLeague(prev => ({ ...prev, [lid]: p }));
            } catch { toast.error("Failed to load participants"); }
        }
    };

    const refreshTournaments = async (lid) => {
        try {
            const r = await api.listLeagueTournaments(lid);
            setTournamentsByLeague(prev => ({ ...prev, [lid]: r }));
        } catch { toast.error("Failed to load tournaments"); }
    };

    // iter45c — Multi-tournament site-admin nav.
    const expandTournaments = async (lid) => {
        if (expandedTournamentsLeagueId === lid) { setExpandedTournamentsLeagueId(null); return; }
        setExpandedTournamentsLeagueId(lid);
        await refreshTournaments(lid);
    };

    const deleteLeague = async (lid) => {
        if (!window.confirm("PERMANENTLY delete this rivalry, tournament, matches, participants, and snapshots? This cannot be undone.")) return;
        try {
            await api.siteDeleteLeague(lid);
            toast.success("League deleted");
            loadAll();
        } catch { toast.error("Failed"); }
    };

    // iter86.40 — Archive: members locked out, record preserved, unarchivable.
    const archiveLeague = async (lg) => {
        const ok = await confirm({
            title: `Archive "${lg.name}"?`,
            body: "Members lose all access (they'll see an 'archived' splash). The full record is preserved and you can unarchive anytime. 12 months after its last tournament ended it becomes delete-eligible.",
            confirmLabel: "Archive",
            cancelLabel: "Cancel",
        });
        if (!ok) return;
        try {
            await api.siteArchiveLeague(lg.id);
            toast.success(`"${lg.name}" archived — members are locked out`);
            loadAll();
        } catch (err) { toast.error(err.response?.data?.detail || "Archive failed"); }
    };

    const unarchiveLeague = async (lg) => {
        try {
            await api.siteUnarchiveLeague(lg.id);
            toast.success(`"${lg.name}" unarchived — member access restored`);
            loadAll();
        } catch (err) { toast.error(err.response?.data?.detail || "Unarchive failed"); }
    };

    // iter87.407 (user-requested) — Close: read-only, not invisible.
    // Site admin can force this at any time, bypassing the tier/
    // tournament-completion gates a rivalry admin's own self-serve
    // close enforces.
    const closeLeague = async (lg) => {
        const ok = await confirm({
            title: `Close "${lg.name}"?`,
            body: "The rivalry becomes read-only for everyone — no new picks, edits, or admin changes — but stays fully viewable (leaderboard, recaps, history). You can unclose it anytime.",
            confirmLabel: "Close",
            cancelLabel: "Cancel",
        });
        if (!ok) return;
        try {
            await api.siteCloseLeague(lg.id);
            toast.success(`"${lg.name}" closed — now read-only`);
            loadAll();
        } catch (err) { toast.error(err.response?.data?.detail || "Close failed"); }
    };

    const uncloseLeague = async (lg) => {
        try {
            await api.siteUncloseLeague(lg.id);
            toast.success(`"${lg.name}" unclosed — back to Live`);
            loadAll();
        } catch (err) { toast.error(err.response?.data?.detail || "Unclose failed"); }
    };

    const endTournament = async (tid, lid) => {
        // iter87.467.2 — End now goes through the same completion routine
        // Mark Complete uses, which immediately closes a FREE-tier rivalry
        // and arms its 30-day archive countdown — tell the admin that
        // up front instead of surprising them with the row vanishing
        // from the Live tab right after confirming.
        const lg = leagues.find(l => l.id === lid);
        const lgTier = lg?.subscription?.effective_tier || lg?.subscription_tier || "free";
        const lgIsProPlus = lg?.pro_plus_ok === true || lgTier === "paid" || lgTier === "comp";
        const ok = await confirm({
            title: "Mark this tournament COMPLETED?",
            body: lgIsProPlus
                ? "Crewmates will see a final state. All edits (scores, teams, drafts, scoring rules) will be blocked until reopened."
                : "Crewmates will see a final state. All edits will be blocked until reopened. Since this rivalry is on the free tier, it will also close immediately (read-only) and start a 30-day countdown to archive.",
            confirmLabel: "End tournament",
            cancelLabel: "Cancel",
        });
        if (!ok) return;
        try {
            await api.siteEndTournament(tid);
            toast.success("Tournament ended");
            loadAll();
            refreshTournaments(lid);
        } catch { toast.error("Failed"); }
    };

    const reopenTournament = async (tid, lid) => {
        const ok = await confirm({
            title: "Reopen this completed tournament?",
            body: "The rivalry admin will be able to edit scores, drafts, and scoring rules again, and any free-tier archive countdown will reset.",
            confirmLabel: "Reopen",
            cancelLabel: "Cancel",
        });
        if (!ok) return;
        try {
            await api.siteReopenTournament(tid);
            toast.success("Tournament reopened — rivalry admin can edit again");
            loadAll();
            refreshTournaments(lid);
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const openScheduleConsole = async (tid) => {
        try {
            const t = await api.getTournament(tid);
            setScheduleConsole({ tid, tournament: t.tournament || t });
        } catch {
            toast.error("Failed to load tournament");
        }
    };

    const deleteTournament = async (tid, lid) => {
        if (!window.confirm("Delete this tournament, all its matches, and unlink it from the rivalry?")) return;
        try {
            await api.siteDeleteTournament(tid);
            toast.success("Tournament deleted");
            loadAll();
            refreshTournaments(lid);
        } catch { toast.error("Failed"); }
    };

    // iter87.601 (user-requested) — Danger-zone safety valve for a
    // Squares board that locked (pool lock and/or Full Game Lock)
    // before the real game actually kicked off, e.g. a vendor-feed
    // glitch. Reverses whichever lock(s) are set and restores exactly
    // the opt-ins each lock dropped.
    const forceUnlockSquares = async (tid, lid) => {
        const ok = await confirm({
            title: "Force-unlock this Squares board?",
            body: "Reverses the pool lock and/or Full Game Lock, restores any crewmates those locks dropped, and clears the digit draw so the next real lock draws fresh. Only use this when the board locked too early (e.g. a bad live score) — not for a game that has genuinely kicked off.",
            confirmLabel: "Force Unlock",
            cancelLabel: "Cancel",
        });
        if (!ok) return;
        try {
            const r = await api.siteForceUnlockSquares(tid);
            toast.success(`Squares board unlocked — restored ${r.restored_count} dropped opt-in${r.restored_count === 1 ? "" : "s"}`);
            refreshTournaments(lid);
        } catch (err) { toast.error(err.response?.data?.detail || "Force unlock failed"); }
    };

    // ---- Bulk selection helpers ----
    const toggleLeagueSelected = (id) => {
        setSelectedLeagueIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    // iter43f — Filter leagues by search query + subscription tier chips.
    // iter87.92 — Unified Pro+ model: `subscription.effective_tier` is the
    // single source of truth. Backend elevates a rivalry to
    // `effective_tier == "paid"` whenever its owner subscribes to Pro+ at
    // the user level, so the "Pro+" chip is a straight `t === "paid"` check.
    // No more `pro_plus_owned` bridge — the resolver handles it.
    const tierCounts = useMemo(() => {
        const acc = { free: 0, paid: 0, comp: 0 };
        leagues.forEach(l => {
            const t = l.subscription?.effective_tier || "free";
            if (acc[t] !== undefined) acc[t] += 1;
        });
        return acc;
    }, [leagues]);
    const _matchesTier = (l, tier) => {
        const t = l.subscription?.effective_tier || "free";
        return t === tier;
    };
    const filteredLeagues = useMemo(() => {
        const q = leagueSearch.trim().toLowerCase();
        return leagues.filter(l => {
            if (leagueView === "archived") {
                if (!l.archived_at) return false;
            } else if (leagueView === "closed") {
                if (!l.closed_at || l.archived_at) return false;
            } else {
                if (l.archived_at || l.closed_at) return false;
            }
            if (leagueTierFilter !== "all" && !_matchesTier(l, leagueTierFilter)) return false;
            if (!q) return true;
            return (l.name || "").toLowerCase().includes(q)
                || (l.invite_code || "").toLowerCase().includes(q)
                || (l.id || "").toLowerCase().includes(q)
                || (l.tournament_summary?.name || "").toLowerCase().includes(q);
        });
    }, [leagues, leagueSearch, leagueTierFilter, leagueView]);
    const archivedCount = useMemo(() => leagues.filter(l => l.archived_at).length, [leagues]);
    const closedCount = useMemo(() => leagues.filter(l => l.closed_at && !l.archived_at).length, [leagues]);
    const liveCount = leagues.length - archivedCount - closedCount;
    // iter87.407 — denominator for the result-count line: leagues in
    // the CURRENT view tab (pre-tier-filter), not the global total,
    // so "Closed" doesn't confusingly read "3 of 750".
    const viewTotal = leagueView === "archived" ? archivedCount : leagueView === "closed" ? closedCount : liveCount;
    const toggleAllLeagues = () => {
        setSelectedLeagueIds(prev => {
            const visibleIds = filteredLeagues.map(l => l.id);
            const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
            const next = new Set(prev);
            if (allVisibleSelected) {
                visibleIds.forEach(id => next.delete(id));
            } else {
                visibleIds.forEach(id => next.add(id));
            }
            return next;
        });
    };
    const allFilteredLeaguesSelected = filteredLeagues.length > 0
        && filteredLeagues.every(l => selectedLeagueIds.has(l.id));
    const clearSelectedLeagues = () => setSelectedLeagueIds(new Set());

    const bulkDeleteLeagues = async () => {
        const ids = Array.from(selectedLeagueIds);
        if (ids.length === 0) return;
        if (!window.confirm(`PERMANENTLY delete ${ids.length} league(s) and all their tournaments, matches, participants, and snapshots? This cannot be undone.`)) return;
        setBulkDeleting(true);
        try {
            const res = await api.siteBulkDeleteLeagues(ids);
            toast.success(`Deleted ${res.deleted_count} league(s)`);
            clearSelectedLeagues();
            loadAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk delete failed");
        }
        setBulkDeleting(false);
    };

    return (
        <>
        {/* iter87.87 — Floating "N leagues selected" bar RETIRED. Bulk
            controls now live in the ribbon directly below the tier pills
            (see `leagues-action-ribbon` further down) so the header no
            longer shifts under the operator on selection. */}

        <div className="space-y-3" data-testid="site-leagues-list">
            {/* iter87.85 — TenantDefaultFlagsCard REMOVED from Rivalry
                Management. Feature grants now live on the per-league
                Custom Subscription tab (Site Admin → Custom Subscriptions). */}
            {/* iter87.82 — Pro+ DNA hero: display header + Create Rivalry CTA */}
            <div className="brutal-card p-5 flex flex-wrap items-center gap-4" data-testid="rivalry-management-hero">
                <div className="w-12 h-12 flex items-center justify-center bg-cyber/10 border border-cyber/40 rounded-xl shrink-0">
                    <Trophy className="w-6 h-6 text-cyber" />
                </div>
                <div className="min-w-0 flex-1">
                    <h1 className="font-display font-black text-2xl sm:text-3xl uppercase tracking-tight leading-none">
                        Rivalry <span className="text-cyber">Management</span>
                    </h1>
                    <p className="text-[11px] text-zinc-500 mt-1.5 uppercase tracking-widest">
                        Every rivalry on the platform · players · tournaments · lifecycle
                    </p>
                </div>
                <button
                    onClick={() => setCreateLeagueOpen(true)}
                    className="brutal-btn-primary text-xs py-2.5 px-4 shadow-brutalYellow"
                    data-testid="open-create-league-modal"
                >
                    <UserPlus className="w-3.5 h-3.5" /> Create Rivalry
                </button>
            </div>
            {/* iter63z'' — Create Rivalry button (Site Admin direct flow) */}
            <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-3">
                    <h2 className="font-display font-black text-lg uppercase tracking-tight">
                        All <span className="text-cyber">Rivalries</span>
                    </h2>
                    {/* iter86.40 / iter87.407 — Live / Closed / Archived tabs */}
                    <div className="flex gap-1.5" data-testid="leagues-view-tabs">
                        {[
                            { k: "live", label: `Live (${liveCount})` },
                            { k: "closed", label: `Closed (${closedCount})` },
                            { k: "archived", label: `Archived (${archivedCount})` },
                        ].map(v => (
                            <button
                                key={v.k}
                                data-testid={`leagues-view-tab-${v.k}`}
                                onClick={() => { setLeagueView(v.k); clearSelectedLeagues(); }}
                                className={`pill border-2 text-[10px] font-bold uppercase py-1 px-2.5 transition-all ${
                                    leagueView === v.k
                                        ? (v.k === "archived" ? "bg-zinc-700 text-white border-zinc-500"
                                            : v.k === "closed" ? "bg-heat text-white border-heat"
                                            : "bg-cyber text-black border-cyber shadow-brutalYellow")
                                        : "bg-transparent text-zinc-400 border-[#333] hover:border-cyber/40 hover:text-cyber"
                                }`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            {/* iter43f — Search box + tier filter chips (iter87.82: glass panel) */}
            <div
                className="brutal-card p-3 flex flex-wrap items-center gap-3 bg-black/40 backdrop-blur border-white/[0.06]"
                data-testid="leagues-filter-bar"
            >
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="leagues-search-input"
                        type="text"
                        placeholder="Search by rivalry name, invite code, id, or tournament…"
                        value={leagueSearch}
                        onChange={(e) => setLeagueSearch(e.target.value)}
                        className="brutal-input w-full pl-9 text-sm"
                    />
                </div>
                <div className="flex gap-1.5 flex-wrap" data-testid="leagues-tier-chips">
                    {[
                        { k: "all", label: `All (${leagues.length})`, activeCls: "bg-zinc-700 text-white border-zinc-500" },
                        { k: "free", label: `Free (${tierCounts.free})`, activeCls: "bg-zinc-800 text-zinc-100 border-zinc-500" },
                        { k: "comp", label: `Comp (${tierCounts.comp})`, activeCls: "bg-volt text-black border-volt" },
                        // iter87.87 — Order: All · Free · Comp · Pro+ (was Paid).
                        // "Paid" tier key stays as-is in the DB; only the display
                        // label changes since operators think of it as Pro+.
                        { k: "paid", label: `Pro+ (${tierCounts.paid})`, activeCls: "bg-cyber text-black border-cyber shadow-brutalYellow" },
                    ].map(c => {
                        const active = leagueTierFilter === c.k;
                        return (
                            <button
                                key={c.k}
                                data-testid={`leagues-tier-chip-${c.k}`}
                                onClick={() => setLeagueTierFilter(c.k)}
                                className={`pill border-2 text-[10px] font-bold uppercase py-1 px-2.5 transition-all ${
                                    active ? c.activeCls : "bg-transparent text-zinc-400 border-[#333] hover:border-cyber/40 hover:text-cyber"
                                }`}
                            >
                                {c.label}
                            </button>
                        );
                    })}
                </div>
            </div>
            {/* iter87.87 — Action ribbon: sits directly under the tier pills.
                Left slot shows a status counter (either "N of M leagues" OR,
                when there's a selection, "N selected"). Right slot shows
                Select-all / Deselect-all + Delete-selected when applicable.
                Replaces the old floating "N leagues selected" card that
                shoved the hero down on every selection. */}
            <div
                className={`flex items-center justify-between flex-wrap gap-2 px-3 py-2 rounded-lg transition-colors ${
                    selectedLeagueIds.size > 0
                        ? "bg-cyber/10 border border-cyber/40"
                        : ""
                }`}
                data-testid="leagues-action-ribbon"
            >
                <p className="text-xs text-zinc-400" data-testid="leagues-result-count">
                    {selectedLeagueIds.size > 0 ? (
                        <>
                            <span className="text-cyber font-black font-display text-sm">
                                {selectedLeagueIds.size}
                            </span>{" "}
                            selected · {filteredLeagues.length} of {viewTotal} shown
                        </>
                    ) : (
                        <>
                            {filteredLeagues.length} of {viewTotal} leagues
                        </>
                    )}
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                    {filteredLeagues.length > 0 && (
                        <button
                            data-testid="select-all-leagues-btn"
                            onClick={toggleAllLeagues}
                            className="brutal-btn-ghost text-xs py-2"
                        >
                            {allFilteredLeaguesSelected
                                ? <><CheckSquare className="w-3.5 h-3.5 text-cyber" /> Deselect all</>
                                : <><Square className="w-3.5 h-3.5" /> Select all</>}
                        </button>
                    )}
                    {selectedLeagueIds.size > 0 && (
                        <>
                            <button
                                data-testid="clear-leagues-selection-btn"
                                onClick={clearSelectedLeagues}
                                className="brutal-btn-ghost text-xs py-2"
                            >
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                            <button
                                data-testid="bulk-delete-leagues-btn"
                                onClick={bulkDeleteLeagues}
                                disabled={bulkDeleting}
                                className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal disabled:opacity-60"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                {bulkDeleting ? "Deleting..." : `Delete ${selectedLeagueIds.size} selected`}
                            </button>
                        </>
                    )}
                </div>
            </div>
            {leagues.length === 0 && (
                <div className="brutal-card p-8 text-center border-dashed" data-testid="leagues-empty">
                    <Sparkles className="w-6 h-6 text-cyber/50 mx-auto mb-2" />
                    <p className="text-zinc-400 text-sm">No rivalries yet.</p>
                    <p className="text-[11px] text-zinc-600 mt-1">Click <strong className="text-cyber">Create Rivalry</strong> above to spin up your first.</p>
                </div>
            )}
            {leagues.length > 0 && filteredLeagues.length === 0 && (
                <div className="brutal-card p-6 text-center border-dashed" data-testid="leagues-empty-filtered">
                    <p className="text-zinc-500 text-sm">No leagues match the current search/filter.</p>
                </div>
            )}
            <div className="space-y-3 overflow-y-auto pr-1" style={{ maxHeight: "70vh" }} data-testid="leagues-scroll-container">
            {filteredLeagues.map(lg => {
                const selected = selectedLeagueIds.has(lg.id);
                const tier = lg.subscription?.effective_tier || "free";
                const tierChip = {
                    paid: "bg-cyber/15 text-cyber border-cyber/40",
                    comp: "bg-volt/15 text-volt border-volt/40",
                    free: "bg-zinc-800 text-zinc-400 border-zinc-600",
                }[tier];
                return (
                <div
                    key={lg.id}
                    className={`brutal-card p-4 transition-all ${selected ? "border-cyber shadow-brutalYellow" : "hover:border-cyber/30"}`}
                    data-testid={`site-league-${lg.id}`}
                >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                            <button
                                type="button"
                                data-testid={`select-league-${lg.id}-checkbox`}
                                onClick={() => toggleLeagueSelected(lg.id)}
                                className="mt-1 shrink-0"
                                aria-label={selected ? "Deselect" : "Select"}
                                aria-pressed={selected}
                            >
                                {selected
                                    ? <CheckSquare className="w-5 h-5 text-cyber" strokeWidth={2.5} />
                                    : <Square className="w-5 h-5 text-zinc-500 hover:text-zinc-300" strokeWidth={2} />}
                            </button>
                            {/* iter87.82 — Pro+ DNA Trophy accent box */}
                            <div className={`w-10 h-10 flex items-center justify-center rounded-xl border shrink-0 ${
                                lg.archived_at
                                    ? "bg-zinc-800/50 border-zinc-700"
                                    : "bg-cyber/10 border-cyber/40"
                            }`}>
                                <Trophy className={`w-5 h-5 ${lg.archived_at ? "text-zinc-500" : "text-cyber"}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <p className="font-display font-black text-lg tracking-tight truncate">{lg.name}</p>
                                    <span className={`pill border text-[9px] py-0 px-1.5 uppercase ${tierChip}`}>
                                        {tier === "paid" ? "Pro+" : tier}
                                    </span>
                                </div>
                                {/* iter87.93 — Owner row + Pro+ backing badge. Makes it
                                    obvious at a glance who's driving the Pro+ elevation
                                    on a stored-comp/free rivalry. */}
                                {lg.admin_email && (
                                    <div
                                        className="flex items-center gap-1.5 mt-1 text-[11px] text-zinc-400"
                                        data-testid={`league-${lg.id}-owner-row`}
                                    >
                                        <User className="w-3 h-3 text-zinc-500 shrink-0" />
                                        <span className="truncate" data-testid={`league-${lg.id}-owner-email`}>
                                            {lg.admin_email}
                                        </span>
                                        {lg.subscription?.pro_plus_backed && (
                                            <span
                                                className="pill bg-cyber/15 border border-cyber/50 text-cyber text-[9px] py-0 px-1.5 uppercase font-bold flex items-center gap-0.5 shrink-0"
                                                data-testid={`league-${lg.id}-owner-pro-plus-badge`}
                                                title="Owner has active Pro+ subscription — every rivalry they own inherits Pro+"
                                            >
                                                <Star className="w-2.5 h-2.5 fill-current" /> Pro+
                                            </span>
                                        )}
                                    </div>
                                )}
                                {/* iter86.40 — Archived metadata + retention chips. iter87.407 — Closed chip. */}
                                {lg.archived_at && (
                                    <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`league-${lg.id}-archive-chips`}>
                                        <span className="pill bg-zinc-800 border border-zinc-600 text-zinc-300 text-[9px] py-0.5">
                                            <Archive className="w-2.5 h-2.5" /> Archived {new Date(lg.archived_at).toLocaleDateString()}{lg.archived_by ? ` by ${lg.archived_by}` : ""}
                                        </span>
                                        {lg.retention && (
                                            <span
                                                data-testid={`league-${lg.id}-retention-chip`}
                                                className={`pill text-[9px] py-0.5 border ${
                                                    lg.retention.past_retention
                                                        ? "bg-heat/20 border-heat text-heat font-bold"
                                                        : "bg-transparent border-zinc-600 text-zinc-400"
                                                }`}
                                                title="12 months after the last tournament ended (tcl.policy.data-retention)"
                                            >
                                                {lg.retention.past_retention
                                                    ? "⚠ Past retention — delete now"
                                                    : `Delete-eligible ${new Date(lg.retention.delete_eligible_at).toLocaleDateString()}`}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {!lg.archived_at && lg.closed_at && (
                                    <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`league-${lg.id}-closed-chips`}>
                                        <span className="pill bg-heat/15 border border-heat/50 text-heat text-[9px] py-0.5">
                                            <Lock className="w-2.5 h-2.5" /> Closed {new Date(lg.closed_at).toLocaleDateString()}{lg.closed_by ? ` by ${lg.closed_by === "system:free-tier-lifecycle" ? "automatically (free tier)" : lg.closed_by}` : ""}
                                        </span>
                                    </div>
                                )}
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1.5 text-[11px] text-zinc-500">
                                    <span className="font-mono bg-black/40 px-1.5 py-0.5 rounded border border-white/[0.06] text-zinc-300">{lg.invite_code}</span>
                                    <span className="text-zinc-700">·</span>
                                    <span>{lg.participant_count} players</span>
                                    {lg.tournament_summary && (
                                        <>
                                            <span className="text-zinc-700">·</span>
                                            <span className="text-zinc-400">{lg.tournament_summary.name}</span>
                                            {/* iter63n — Synthesized display status (live/ready/setup/completed) */}
                                            {(() => {
                                                const ds = (lg.tournament_summary.display_status || lg.tournament_summary.status || "setup").toLowerCase();
                                                const styles = {
                                                    live: "bg-neon/10 border-neon/60 text-neon",
                                                    ready: "bg-cyber/10 border-cyber/60 text-cyber",
                                                    final: "bg-cyber/10 border-cyber/60 text-cyber",
                                                    completed: "bg-zinc-800 border-zinc-600 text-zinc-400",
                                                    setup: "bg-transparent border-[#444] text-zinc-400",
                                                };
                                                return (
                                                    <span
                                                        data-testid={`league-${lg.id}-status-pill`}
                                                        className={`pill text-[9px] py-0 px-1.5 border ${styles[ds] || styles.setup}`}
                                                    >
                                                        {ds}
                                                    </span>
                                                );
                                            })()}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button
                                data-testid={`expand-league-${lg.id}-btn`}
                                onClick={() => expandLeague(lg.id)}
                                className={`brutal-btn-ghost text-xs py-2 ${expandedLeagueId === lg.id ? "border-cyber/60 text-cyber" : ""}`}>
                                {expandedLeagueId === lg.id ? "Collapse" : "Players"}
                            </button>
                            <button
                                data-testid={`expand-tournaments-${lg.id}-btn`}
                                onClick={() => expandTournaments(lg.id)}
                                className={`brutal-btn-ghost text-xs py-2 ${expandedTournamentsLeagueId === lg.id ? "border-cyber/60 text-cyber" : ""}`}
                                title="View every tournament in this rivalry (active + completed) and manage them individually">
                                <Calendar className="w-3 h-3" />
                                {expandedTournamentsLeagueId === lg.id ? "Collapse" : "Tournaments"}
                            </button>
                            {/* iter86.74 — Features dropdown removed: custom-template
                                features are now selected by the league admin in the
                                tournament wizard, governed globally via Admin Ops →
                                Custom Template Features. */}
                            {/* iter87.407 — Close / Unclose (site-admin only) */}
                            {!lg.archived_at && (
                                lg.closed_at ? (
                                    <button
                                        data-testid={`unclose-league-${lg.id}-btn`}
                                        onClick={() => uncloseLeague(lg)}
                                        className="brutal-btn-ghost text-xs py-2"
                                        title="Revert to Live — restores write access">
                                        <Unlock className="w-3 h-3" /> Unclose
                                    </button>
                                ) : (
                                    <button
                                        data-testid={`close-league-${lg.id}-btn`}
                                        onClick={() => closeLeague(lg)}
                                        className="brutal-btn-ghost text-xs py-2"
                                        title="Make read-only — still viewable, reversible">
                                        <Lock className="w-3 h-3" /> Close
                                    </button>
                                )
                            )}
                            {/* iter86.40 — Archive / Unarchive */}
                            {lg.archived_at ? (
                                <button
                                    data-testid={`unarchive-league-${lg.id}-btn`}
                                    onClick={() => unarchiveLeague(lg)}
                                    className="brutal-btn-ghost text-xs py-2"
                                    title="Restore member access">
                                    <ArchiveRestore className="w-3 h-3" /> Unarchive
                                </button>
                            ) : (
                                <button
                                    data-testid={`archive-league-${lg.id}-btn`}
                                    onClick={() => archiveLeague(lg)}
                                    className="brutal-btn-ghost text-xs py-2"
                                    title="Lock members out but keep the full record (reversible)">
                                    <Archive className="w-3 h-3" /> Archive
                                </button>
                            )}
                            <button
                                data-testid={`delete-league-${lg.id}-btn`}
                                onClick={() => deleteLeague(lg.id)}
                                className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal">
                                <Trash2 className="w-3 h-3" /> League
                            </button>
                        </div>
                    </div>
                    {expandedLeagueId === lg.id && (
                        <LeagueParticipantsPanel
                            league={lg}
                            participants={participantsByLeague[lg.id] || []}
                            onParticipantsChanged={(p) => setParticipantsByLeague(prev => ({ ...prev, [lg.id]: p }))}
                            onLeaguesReload={loadAll}
                        />
                    )}
                    {expandedTournamentsLeagueId === lg.id && (
                        <LeagueTournamentsPanel
                            league={lg}
                            tdata={tournamentsByLeague[lg.id]}
                            onOpenSchedule={openScheduleConsole}
                            onOpenSnapshots={setSnapshotsModal}
                            onOpenRenamePreview={setRenamePreview}
                            onEnd={(tid) => endTournament(tid, lg.id)}
                            onReopen={(tid) => reopenTournament(tid, lg.id)}
                            onDelete={(tid) => deleteTournament(tid, lg.id)}
                            onForceUnlockSquares={(tid) => forceUnlockSquares(tid, lg.id)}
                        />
                    )}
                </div>
                );
            })}
            </div>
            {/* iter87.199.6 — CrownPointsSandbox mount removed with the
                Crown Race excise. The Title Race replacement will need
                its own sandbox later; nothing to mount in the
                meantime. */}
        </div>

        {/* iter86.74 — Bulk features modal removed with the per-rivalry
            Features dropdown (custom features now wizard-selected). */}
        <SiteAdminCreateLeagueModal
            open={createLeagueOpen}
            onClose={() => setCreateLeagueOpen(false)}
            onCreated={() => loadAll()}
        />
        {scheduleConsole && (
            <SiteAdminSchedule
                tournamentId={scheduleConsole.tid}
                tournament={scheduleConsole.tournament}
                onClose={() => setScheduleConsole(null)}
            />
        )}
        {snapshotsModal && (
            <SiteAdminDraftSnapshots
                tournamentId={snapshotsModal.tid}
                tournamentName={snapshotsModal.name}
                onClose={() => setSnapshotsModal(null)}
            />
        )}
        <SiteAdminRenamePreviewModal
            open={!!renamePreview}
            onOpenChange={(o) => { if (!o) setRenamePreview(null); }}
            tournamentId={renamePreview?.tid}
            tournamentName={renamePreview?.name}
        />
        {confirmHost}
        </>
    );
}
